import React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { formatWordWheelQuestPrice } from "../wordWheelQuestCommerce";
import useWordWheelWallet from "../hooks/useWordWheelWallet";
import { WORD_WHEEL_HINT_COST } from "../lib/points";

function BalanceRow({ label, value, loading, suffix = "" }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 1,
                py: 0.75,
            }}
        >
            <Typography variant="body2" sx={{ fontWeight: 600, color: "rgba(15, 61, 54, 0.75)" }}>
                {label}
            </Typography>
            {loading ? (
                <CircularProgress size={16} sx={{ color: "#059669" }} />
            ) : (
                <Typography variant="body1" sx={{ fontWeight: 800, color: "#0f3d36" }}>
                    {value}
                    {suffix}
                </Typography>
            )}
        </Box>
    );
}

function ProductButtons({ products, purchasingProductId, onPurchase, lowBalanceLabel }) {
    if (!products?.length) {
        return null;
    }

    return (
        <Box sx={{ mt: 1.25 }}>
            <Typography variant="caption" sx={{ display: "block", color: "rgba(15, 61, 54, 0.65)", mb: 0.75 }}>
                {lowBalanceLabel}
            </Typography>
            <Stack spacing={0.75}>
                {products.map((product) => {
                    const busy = purchasingProductId === product.productId;
                    return (
                        <Button
                            key={product.productId}
                            variant="outlined"
                            disabled={Boolean(purchasingProductId)}
                            onClick={() => onPurchase(product)}
                            sx={{
                                justifyContent: "space-between",
                                textTransform: "none",
                                borderColor: "rgba(5, 150, 105, 0.35)",
                                color: "#0f3d36",
                                fontWeight: 700,
                                py: 1,
                                px: 1.25,
                            }}
                        >
                            <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", justifyContent: "space-between" }}>
                                <span>
                                    {product.label} · +{product.creditsAwarded}
                                </span>
                                <span>{busy ? "Purchasing…" : formatWordWheelQuestPrice(product.priceCad)}</span>
                            </Box>
                        </Button>
                    );
                })}
            </Stack>
        </Box>
    );
}

export default function WordWheelCreditsPointsSection() {
    const wallet = useWordWheelWallet();

    return (
        <Box sx={{ mb: 2 }}>
            {(wallet.error || wallet.purchaseError) && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                    {wallet.purchaseError || wallet.error}
                </Alert>
            )}

            {!wallet.nativePurchasesAvailable && !wallet.loading && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                    Purchases open in the Word Wheel Quest iPhone app (RevenueCat).
                </Alert>
            )}

            <Box
                sx={{
                    borderRadius: 2,
                    border: "1px solid rgba(15, 61, 54, 0.12)",
                    bgcolor: "#fff",
                    px: 1.5,
                    py: 1,
                    mb: 1.25,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#059669", mb: 0.5 }}>
                    Credits
                </Typography>
                <BalanceRow
                    label="Wallet balance"
                    value={wallet.creditBalance}
                    loading={wallet.loading}
                    suffix=" credits"
                />
                <Typography variant="caption" sx={{ display: "block", color: "rgba(15, 61, 54, 0.6)", mt: 0.5 }}>
                    Used for premium features and hint boosts when your puzzle budget runs out.
                </Typography>
                {wallet.showCreditPurchase && (
                    <ProductButtons
                        products={wallet.creditProducts}
                        purchasingProductId={wallet.purchasingProductId}
                        onPurchase={wallet.purchaseCreditPack}
                        lowBalanceLabel="Balance is low — purchase credits:"
                    />
                )}
            </Box>

            <Box
                sx={{
                    borderRadius: 2,
                    border: "1px solid rgba(15, 61, 54, 0.12)",
                    bgcolor: "#fff",
                    px: 1.5,
                    py: 1,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#059669", mb: 0.5 }}>
                    Coins
                </Typography>
                <BalanceRow
                    label="Lifetime puzzle coins"
                    value={wallet.lifetimePoints}
                    loading={wallet.loading}
                />
                <BalanceRow
                    label="Hint cost"
                    value={WORD_WHEEL_HINT_COST}
                    loading={false}
                    suffix=" coins per hint"
                />
                <Typography variant="caption" sx={{ display: "block", color: "rgba(15, 61, 54, 0.6)", mt: 0.5 }}>
                    Each puzzle earns coins by word length. Hints spend puzzle coins first, then wallet credits.
                </Typography>
                {wallet.showPointPurchase && (
                    <ProductButtons
                        products={wallet.pointProducts}
                        purchasingProductId={wallet.purchasingProductId}
                        onPurchase={wallet.purchasePointPack}
                        lowBalanceLabel={`Need at least ${WORD_WHEEL_HINT_COST} coins for a hint — purchase coin packs:`}
                    />
                )}
            </Box>
        </Box>
    );
}
